import { crmGet, essDel, essGet, essPatch, essPost, essPut, imsGet } from "../api/api";

const BASE = "/local-expense-claims";
const LINES = "/local-expense-claim-lines";

/** The metrics master is queried by category; this is the API's own value. */
const CURRENCY_METRIC_CATEGORY = "Currency";

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  });
  return search.toString();
}

/** Rows live under data.content when paged, or data itself when not. */
function rowsOf(response, key) {
  const data = response?.data ?? {};
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.content)) return data.content;
  if (key && Array.isArray(data[key])) return data[key];
  return [];
}

/** Keys the caller left undefined are dropped so a PUT states only real changes. */
function compact(body) {
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));
}

/* ----------------------------------------------------------------- claims */

/** POST /local-expense-claims/add — ESS07A. `payload.lineDetail` carries the trips. */
export async function createClaim(payload) {
  const response = await essPost(`${BASE}/add`, compact(payload), "ESS07A");
  return response?.data ?? null;
}

/**
 * PUT /local-expense-claims/update/{claimId} — ESS07U.
 *
 * `lineDetail` is the claim's complete set of trips, not a delta: a line the
 * user removed is simply absent from the array.
 */
export async function updateClaim(claimId, payload) {
  const response = await essPut(`${BASE}/update/${claimId}`, compact(payload), "ESS07U");
  return response?.data ?? null;
}

/** GET /local-expense-claims/{claimId} — ESS07S */
export async function getClaimById(claimId) {
  const response = await essGet(`${BASE}/${claimId}`, "ESS07S");
  return response?.data ?? null;
}

/** DELETE /local-expense-claims/delete/{claimId} — ESS07D. Cascades to its lines. */
export async function deleteClaim(claimId) {
  await essDel(`${BASE}/delete/${claimId}`, "ESS07D");
  return claimId;
}

/**
 * GET /local-expense-claims/search/employee?empId= — ESS07S
 *
 * Every ESS claim belongs to the signed-in employee, so the list reads the
 * employee endpoint. There is no combined employee + status + month filter, so
 * the screen filters and pages the returned rows itself.
 */
export async function fetchMyClaims(empId) {
  const response = await essGet(
    `${BASE}/search/employee?${query({ empId, page: 0, size: 10, sortBy: "claimId", sortDir: "DESC" })}`,
    "ESS07S"
  );
  return rowsOf(response);
}

/* ------------------------------------------------------------ trip lines */

/**
 * GET /local-expense-claim-lines/search/claim?claimId= — ESS07S
 *
 * Sorted by the primary key server-side (the only column guaranteed to be
 * sortable), then ordered by line_no here, which is what the grid renders.
 */
export async function fetchTripLines(claimId) {
  const response = await essGet(
    `${LINES}/search/claim?${query({
      claimId,
      page: 0,
      size: 10,
      sortBy: "claimLineId",
      sortDir: "ASC",
    })}`,
    "ESS07S"
  );

  return rowsOf(response).sort((a, b) => Number(a.lineNo ?? 0) - Number(b.lineNo ?? 0));
}

/* ---------------------------------------------------------------- lookups */

/**
 * GET /saas/ims/product-metrics/search/category?category=Currency — IMS04S
 *
 * Feeds the currency selector next to every amount field. Only the id and the
 * abbreviation are kept: the id is what org_expense_claim.metric_id stores, the
 * abbreviation is what the selector shows.
 */
export async function fetchCurrencies() {
  const response = await imsGet(
    `/product-metrics/search/category?${query({
      category: CURRENCY_METRIC_CATEGORY,
      page: 0,
      size: 30,
      sortBy: "name",
      sortDir: "ASC",
    })}`,
    "IMS04S"
  );

  return rowsOf(response)
    .filter((row) => row.isActive !== false && row.metricId != null)
    .map((row) => ({
      metricId: row.metricId,
      abbreviation: row.abbreviation,
      name: row.name,
    }));
}

/** GET /saas/crm/customer-info/search — CRM01S. Called only when a trip line asks for it. */
export async function fetchCustomers() {
  const response = await crmGet(
    `/customer-info/search?${query({ page: 0, size: 200, sortBy: "companyName", sortDir: "ASC" })}`,
    "CRM01S"
  );

  return rowsOf(response, "customers")
    .map((row) => ({ id: row.customerId ?? row.id, name: row.companyName ?? row.name }))
    .filter((row) => row.id != null);
}

/** GET /saas/crm/customer-locations/search/customer — CRM04S. */
export async function fetchCustomerLocations(customerId) {
  const response = await crmGet(
    `/customer-locations/search/customer?${query({
      customerId,
      page: 0,
      size: 200,
      sortBy: "locationId",
      sortDir: "ASC",
    })}`,
    "CRM03S"
  );

  return rowsOf(response, "locations")
    .map((row) => ({ id: row.locationId ?? row.id, name: row.locationName ?? row.name }))
    .filter((row) => row.id != null);
}

/* ------------------------------------------------------- approval workflow */

/** UI action ids -> the values the action endpoint records. */
const APPROVER_ACTION_WIRE = { approve: "Approve", reject: "Reject", sendBack: "Send_back" };

/** Thrown by the readers/writers that have no endpoint yet. */
export class ApprovalApiUnavailableError extends Error {
  constructor() {
    super("APPROVAL_API_UNAVAILABLE");
    this.name = "ApprovalApiUnavailableError";
    this.messageKey = "conveyanceApproval.apiUnavailableError";
  }
}

/** Flip to true when the list/proof/export endpoints below are published. */
export const APPROVAL_API_READY = false;

const emptyPage = (extra = {}) => ({
  rows: [],
  totalCount: 0,
  filterOptions: { submitters: [], departments: [], months: [] },
  ...extra,
});

/** GET /local-expense-claims/{claimId} — the claim, with its approval trail. */
export async function fetchClaimForApproval(claimId) {
  return getClaimById(claimId);
}

/** The claim's trip lines (reuses the ESS reader). */
export async function fetchClaimLines(claimId) {
  return fetchTripLines(claimId);
}

/** POST /local-expense-claims/{claimId}/action — ESS07U. Approve / Reject / Send back. */
export async function submitApproverAction({ claimId, action, reason }) {
  const response = await essPost(
    `${BASE}/${claimId}/action`,
    { action: APPROVER_ACTION_WIRE[action] ?? action, reasonComment: reason || null },
    "ESS07U"
  );
  return response?.data ?? null;
}

/** PATCH /local-expense-claims/mark-processed — ESS07U. Finance bulk flip to Processed. */
export async function markClaimsProcessed(claimIds) {
  const response = await essPatch(`${BASE}/mark-processed`, { claimIds }, "ESS07U");
  return response?.data ?? null;
}

/* Endpoints not published yet — reportee/tenant list, proofs, export. */
export async function fetchTeamClaims() {
  return emptyPage({ pendingCount: 0 });
}

export async function fetchAllClaims() {
  return emptyPage({ totals: [] });
}

export async function fetchClaimProofs() {
  return [];
}

export async function exportClaims() {
  throw new ApprovalApiUnavailableError();
}

export const conveyanceClaimService = {
  createClaim,
  updateClaim,
  getClaimById,
  deleteClaim,
  fetchMyClaims,
  fetchTripLines,
  fetchCurrencies,
  fetchCustomers,
  fetchCustomerLocations,
  fetchClaimForApproval,
  fetchClaimLines,
  submitApproverAction,
  markClaimsProcessed,
  fetchTeamClaims,
  fetchAllClaims,
  fetchClaimProofs,
  exportClaims,
  APPROVAL_API_READY,
};

export default conveyanceClaimService;
